import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../api/axios";
import { companyID } from "../auth";

const employmentPriority = {
  permanent: 1,
  probationary: 2,
  intern: 3,
  pip: 4,
  on_notice: 5,
  separated: 6,
  terminated: 7,
};

const getEmploymentPriority = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  return employmentPriority[normalizedStatus] || 999;
};

const sortEmployeesByEmploymentStatus = (employees = []) => {
  return employees
    .map((employee, index) => ({ employee, index }))
    .sort((a, b) => {
      const priorityA = getEmploymentPriority(a.employee?.employment_status);
      const priorityB = getEmploymentPriority(b.employee?.employment_status);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return a.index - b.index;
    })
    .map(({ employee }) => employee);
};

export const useEmployees = (params) => {
  const [employees, setEmployees] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const company_id = companyID();
  const defaultParams = useMemo(() => params || {}, [JSON.stringify(params || {})]);

  const fetchEmployees = useCallback(async (paramsToUse = defaultParams) => {
    if (!company_id) return;
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      Object.entries(paramsToUse).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, v));
        } else if (value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });
      const res = await api.get(
        `employees/employeeDetails/${company_id}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
      );
      if (Array.isArray(res?.data)) {
        const sortedEmployees = sortEmployeesByEmploymentStatus(res.data);
        setEmployees(sortedEmployees);
        setOptions(
          sortedEmployees.map((emp) => ({
            value: emp.id,
            label: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
            employee: emp,
          }))
        );
      } else {
        setEmployees([]);
        setOptions([]);
      }
    } catch (err) {
      setEmployees([]);
      setOptions([]);
      console.error("Employee fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [company_id, defaultParams]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  return {
    employees,
    options,
    loading,
    refetch: fetchEmployees,
  };
};
